import { Injectable } from '@angular/core';
import { IQBoostViewStore } from '@maximizer/ai/data-access';
import {
  DefaultActionValue,
  DefaultJourneyValue,
  TrackEventService,
} from '@maximizer/core/shared/data-access';

export enum EventAction {
  Clicked = DefaultActionValue,
  Opened = 'opened',
  Closed = 'closed',
  Saved = 'saved',
  Executed = 'executed',
  Started = 'started',
  Summarized = 'summarized',
  Selected = 'selected',
  MessageSent = 'message-sent',
  Shown = 'shown',
  Enabled = 'enabled',
  Disabled = 'disabled',
  Changed = 'changed',
}

export enum EventModule {
  Ai = 'ai',
  AiMeetingPreparationSummary = 'ai-meeting-preparation-summary',
  AiTimelineSummary = 'ai-timeline-summary',
  AiIQBoost = 'ai-iq-boost',
  AiHub = 'ai-hub',
}

export enum EventJourney {
  Action = DefaultJourneyValue,
  Feedback = 'feedback',
  RecommendedActions = 'recommended-actions',
  Summarization = 'summarization',
  SecurityCenter = 'security-center',
  Report = 'report',
  Prompt = 'prompt',
  AskQuestion = 'ask-question',
  Help = 'help',
  Error = 'error',
}

abstract class EventTracker {
  constructor(
    public readonly module: EventModule,
    private readonly tracker: TrackEventService,
  ) {}

  trackEvent(
    journey: EventJourney,
    action: EventAction,
    metadata?: Record<string, string>,
  ): void {
    this.tracker.trackEvent({
      module: this.module,
      journey,
      action,
      metadata,
    });
  }
}

@Injectable()
export class TimelineSummaryEventTracker extends EventTracker {
  constructor(tracker: TrackEventService) {
    super(EventModule.AiTimelineSummary, tracker);
  }
}

@Injectable()
export class MeetingPreparationSummaryEventTracker extends EventTracker {
  constructor(tracker: TrackEventService) {
    super(EventModule.AiMeetingPreparationSummary, tracker);
  }
}

@Injectable()
export class AiEventTracker extends EventTracker {
  constructor(tracker: TrackEventService) {
    super(EventModule.Ai, tracker);
  }
}

@Injectable()
export class IQBoostEventTracker extends EventTracker {
  constructor(
    tracker: TrackEventService,
    private readonly store: IQBoostViewStore
  ) {
    super(EventModule.AiIQBoost, tracker);
  }

  override trackEvent(
    journey: EventJourney,
    action: EventAction,
    metadata?: Record<string, string>,
  ): void {
    const anonymized = this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures.anonymized && 
      this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures.anonymized;
    const useGlobalServers = this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures.useGlobalServers && 
      this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures.useGlobalServers;
    const useWebData = this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures.useWebData && 
      this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures.useWebData;

    const appendMetadata = {
      mode: this.store.useAllHouseholds() ? 'all' : 'single',
      anonymizer: anonymized ? 'on' : 'off',
      modelUsed: useGlobalServers ? 'global' : 'local',
      dataSource: useWebData ? 'web' : 'maximizer',
    };

    super.trackEvent(journey, action, { ...appendMetadata, ...metadata });
  }
}

@Injectable()
export class AiHubEventTracker extends EventTracker {
  constructor(tracker: TrackEventService) {
    super(EventModule.AiHub, tracker);
  }
}
